<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength, required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  ALLOWED_FILE_TYPES,
  MAXIMUM_FILE_UPLOAD_SIZE,
} from 'shared/constants/messages';
import FileUpload from 'vue-upload-component';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getLINEInboxes'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [[]],
  selectedAudienceOperator: [],
};

const state = reactive({ ...initialState });

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.LINE.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
};

const handleCancel = () => emit('cancel');

const attachedFiles = ref([]);

const prepareCampaignDetails = () => {
  let audienceConditions = [];
  let currentAndValues = [];
  for (let i = 0; i < state.selectedAudienceOperator.length; i += 1) {
    if (
      state.selectedAudienceOperator[i] === 'or' &&
      currentAndValues.length === 0
    ) {
      audienceConditions = [
        ...audienceConditions,
        ...state.selectedAudience[i].map(item => item.value),
      ];
      if (i === state.selectedAudienceOperator.length - 1)
        audienceConditions = [
          ...audienceConditions,
          ...state.selectedAudience[i + 1].map(item => item.value),
        ];
    } else if (
      state.selectedAudienceOperator[i] === 'or' &&
      currentAndValues.length > 0
    ) {
      audienceConditions.push({
        operator: 'and',
        values: JSON.stringify(currentAndValues),
      });
      currentAndValues = [];
    } else if (state.selectedAudienceOperator[i] === 'and') {
      if (currentAndValues.length === 0)
        currentAndValues.push(
          state.selectedAudience[i].map(item => item.value)
        );
      currentAndValues.push(
        state.selectedAudience[i + 1].map(item => item.value)
      );
      if (i === state.selectedAudienceOperator.length - 1)
        audienceConditions = [
          ...audienceConditions,
          {
            operator: 'and',
            values: JSON.stringify(currentAndValues),
          },
        ];
    }
  }

  if (attachedFiles.value.length > 0) {
    let campaignDetails = new FormData();
    campaignDetails.append('title', state.title);
    campaignDetails.append('message', state.message);
    campaignDetails.append('inbox_id', state.inboxId);
    campaignDetails.append(
      'scheduled_at',
      formatToUTCString(state.scheduledAt)
    );
    campaignDetails.append(
      'audience',
      JSON.stringify(
        state.selectedAudience.flat().map(item => ({
          id: item.value,
          type: 'Label',
        }))
      )
    );
    campaignDetails.append(
      'trigger_rules',
      JSON.stringify({ audience_conditions: audienceConditions })
    );
    attachedFiles.value.forEach(
      file =>
        file.resource &&
        campaignDetails.append('attachments[]', file.resource.file)
    );

    return campaignDetails;
  }

  return {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience.flat().map(item => ({
      id: item.value,
      type: 'Label',
    })),
    trigger_rules: {
      audience_conditions: audienceConditions,
    },
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

const attachFile = ({ blob, file }) => {
  const reader = new FileReader();
  reader.readAsDataURL(file.file);
  reader.onloadend = () => {
    attachedFiles.value.push({
      resource: blob || file,
      thumb: reader.result,
      blobSignedId: blob ? blob.signed_id : undefined,
    });
  };
};

const onIndirectFileUpload = file => {
  const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = MAXIMUM_FILE_UPLOAD_SIZE;
  if (!file) {
    return;
  }
  if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
    attachFile({ file });
  } else {
    useAlert(
      t('CONVERSATION.FILE_SIZE_LIMIT', {
        MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
      })
    );
  }
};

const removeAttachment = itemIndex => {
  attachedFiles.value = attachedFiles.value.filter(
    (item, index) => itemIndex !== index
  );
};

const appendNewPeerAudience = () => {
  state.selectedAudience.push([]);
  state.selectedAudienceOperator.push('and');
};

const removePeerAudience = idx => {
  state.selectedAudience.splice(idx, 1);
  state.selectedAudienceOperator.splice(idx, 1);
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.LINE.CREATE.FORM.TITLE.LABEL')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
      :placeholder="t('CAMPAIGN.LINE.CREATE.FORM.TITLE.PLACEHOLDER')"
    />

    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.LINE.CREATE.FORM.MESSAGE.LABEL')"
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
      :placeholder="t('CAMPAIGN.LINE.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      show-character-count
    />

    <div class="flex justify-between w-full">
      <FileUpload
        v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        :accept="ALLOWED_FILE_TYPES"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        :drop="false"
        :drop-directory="false"
        :multiple="false"
        :size="4096 * 4096"
        @input-file="onIndirectFileUpload"
      >
        <woot-button
          :title="t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          class-names="button--upload"
          color-scheme="secondary"
          emoji="📎"
          icon="attach"
          size="small"
          variant="smooth"
        />
      </FileUpload>
      <div v-if="attachedFiles.length > 0" class="attachment-preview-box">
        <AttachmentPreview
          :attachments="attachedFiles"
          class="flex-col"
          @remove-attachment="removeAttachment"
        />
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12" for="inbox">
        {{ t('CAMPAIGN.LINE.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :has-error="!!formErrors.inbox"
        :message="formErrors.inbox"
        :options="inboxOptions"
        :placeholder="t('CAMPAIGN.LINE.CREATE.FORM.INBOX.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12" for="audience">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <div
        class="w-full p-4 border border-solid rounded-lg bg-slate-25 dark:bg-slate-700 border-slate-50 dark:border-slate-700"
      >
        <div v-for="(selectedItem, i) in state.selectedAudience" :key="i">
          <template v-if="i > 0">
            <div class="flex items-center justify-center relative my-2.5 mx-0">
              <hr
                class="w-full absolute border-b border-solid border-slate-75 dark:border-slate-800"
              />
              <select
                v-model="state.selectedAudienceOperator[i - 1]"
                class="bg-white dark:bg-slate-900 mb-0 w-auto relative text-slate-800 dark:text-slate-100 border-slate-75 dark:border-slate-600"
              >
                <option value="and">
                  {{ $t('FILTER.QUERY_DROPDOWN_LABELS.AND') }}
                </option>
                <option value="or">
                  {{ $t('FILTER.QUERY_DROPDOWN_LABELS.OR') }}
                </option>
              </select>
            </div>
          </template>
          <div class="flex items-center multiselect-wrap--small">
            <multiselect
              v-model="state.selectedAudience[i]"
              :clear-on-select="false"
              :close-on-select="false"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              hide-selected
              multiple
              :options="audienceList"
              :placeholder="
                $t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')
              "
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              class="no-margin mr-1"
              label="label"
              selected-label
              track-by="value"
              @blur="v$.selectedAudience.$touch"
              @select="v$.selectedAudience.$touch"
            />
            <woot-button
              v-if="state.selectedAudience.length > 1"
              :key="`close-${i}`"
              color-scheme="secondary"
              icon="dismiss"
              variant="clear"
              @click.stop="() => removePeerAudience(i)"
            />
          </div>
        </div>
        <div class="mt-2">
          <woot-button
            color-scheme="success"
            icon="add"
            size="small"
            variant="smooth"
            @click.prevent="appendNewPeerAudience"
          >
            {{
              $t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.AUDIENCE_BUTTON_LABEL')
            }}
          </woot-button>
        </div>
        <span v-if="v$.selectedAudience.$error" class="audience-error">
          {{ $t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ERROR') }}
        </span>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.LINE.CREATE.FORM.SCHEDULED_AT.LABEL')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.LINE.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      type="datetime-local"
    />

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        :label="t('CAMPAIGN.LINE.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 n-blue-text hover:bg-n-alpha-3"
        color="slate"
        type="button"
        variant="faded"
        @click="handleCancel"
      />
      <Button
        :disabled="isCreating || isSubmitDisabled"
        :is-loading="isCreating"
        :label="t('CAMPAIGN.LINE.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
      />
    </div>
  </form>
</template>
